import {
  HOUR_DECIMAL_PLACES,
  formatDisplayHours,
  isHoursPositive,
  isHoursZero,
  parseHour,
  roundHours,
} from '../timeTrackingGridHelpers';

export {
  HOUR_DECIMAL_PLACES,
  isHoursPositive,
  isHoursZero,
  parseHour,
  roundHours,
};

export const hourTypes = ['hours', 'otHours', 'doubleOTHours'];

export const getProjectIdFromColumn = (column) => column?.project?.id ?? null;

export const getSameProjectCostcodedColumns = (columns, activeProjectId) => (
  (columns || []).filter((column) => (
    getProjectIdFromColumn(column) === activeProjectId && column.costcode?.id
  ))
);

export const sumCellHours = (cell) => hourTypes.reduce(
  (total, hourType) => total + parseHour(cell?.[hourType]),
  0,
);

export const formatHours = (value, decimalPlaces = HOUR_DECIMAL_PLACES) => (
  formatDisplayHours(value, decimalPlaces)
);

/**
 * When moving hours into a cost-coded bucket `destHourType`, pull unassigned funds in this order.
 * REG → OT → DT; OT → DT → REG; DT → OT → REG.
 */
export function unassignedFundingOrder(destHourType) {
  const order = {
    hours: ['hours', 'otHours', 'doubleOTHours'],
    otHours: ['otHours', 'doubleOTHours', 'hours'],
    doubleOTHours: ['doubleOTHours', 'otHours', 'hours'],
  };
  return order[destHourType] || order.hours;
}

export function cloneDeep(value) {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
}

function readCell(row, colId) {
  const cell = row?.[colId] || {};
  return {
    hours: parseHour(cell.hours) || 0,
    otHours: parseHour(cell.otHours) || 0,
    doubleOTHours: parseHour(cell.doubleOTHours) || 0,
  };
}

function writeCell(row, colId, nums) {
  return {
    ...row,
    [colId]: {
      ...(row[colId] || {}),
      hours: nums.hours,
      otHours: nums.otHours,
      doubleOTHours: nums.doubleOTHours,
    },
  };
}

function ensureLedgerEntry(ledger, rowId, destColId, destHourType) {
  const rowMap = ledger[rowId] || {};
  const colMap = rowMap[destColId] || {};
  const existing = colMap[destHourType];
  const entry = existing
    ? { ...existing, stack: [...existing.stack] }
    : { stack: [] };

  const nextLedger = {
    ...ledger,
    [rowId]: {
      ...rowMap,
      [destColId]: {
        ...colMap,
        [destHourType]: entry,
      },
    },
  };

  return { ledger: nextLedger, entry };
}

function pushTake(entry, sourceType, amount) {
  if (amount <= 0) return;
  const { stack } = entry;
  const last = stack[stack.length - 1];
  if (last && last.sourceType === sourceType) {
    stack[stack.length - 1] = { ...last, amount: last.amount + amount };
  } else {
    stack.push({ sourceType, amount });
  }
}

function sumStack(entry) {
  if (!entry?.stack?.length) return 0;
  return entry.stack.reduce((total, seg) => total + seg.amount, 0);
}

function popFromStack(entry, amount) {
  const refunds = { hours: 0, otHours: 0, doubleOTHours: 0 };
  const { stack } = entry;
  let left = amount;
  while (left > 0 && stack.length) {
    const top = stack[stack.length - 1];
    const chunk = Math.min(left, top.amount);
    refunds[top.sourceType] += chunk;
    left -= chunk;
    const remainingAmount = top.amount - chunk;
    if (remainingAmount <= 0) {
      stack.pop();
    } else {
      stack[stack.length - 1] = { ...top, amount: remainingAmount };
    }
  }
  return refunds;
}

export function clearLedgerForRow(ledger, rowId) {
  const newLedger = cloneDeep(ledger);
  delete newLedger[rowId];
  return newLedger;
}

export function getAddedCostcodeColumnRowIds(originalColumns = [], pendingColumns = []) {
  const originalRowIds = new Set(
    originalColumns
      .filter((column) => column.costcode?.id)
      .map((column) => column.rowId),
  );

  return pendingColumns
    .filter((column) => column.costcode?.id && !originalRowIds.has(column.rowId))
    .map((column) => column.rowId);
}

export function clearLedgerForColumnIds(ledger, columnRowIds = []) {
  const columnIds = new Set(columnRowIds);
  if (!columnIds.size) return ledger;

  const nextLedger = cloneDeep(ledger);
  Object.keys(nextLedger).forEach((rowId) => {
    const rowMap = nextLedger[rowId];
    if (!rowMap) return;

    Object.keys(rowMap).forEach((colId) => {
      if (columnIds.has(colId)) delete rowMap[colId];
    });

    if (Object.keys(rowMap).length === 0) {
      delete nextLedger[rowId];
    }
  });

  return nextLedger;
}

export function addHours({
  gridState,
  ledger,
  rowId,
  unassignedColumnRowId,
  destinationColumnRowId,
  destHourType,
  delta,
  sameProjectCostcodedColumnIds,
}) {
  const colSet = new Set(sameProjectCostcodedColumnIds || []);
  if (delta <= 0 || !colSet.has(destinationColumnRowId)) {
    return { gridState, ledger, applied: 0 };
  }

  const nextGrid = cloneDeep(gridState);
  const values = nextGrid.values || [];
  const rowIndex = values.findIndex((r) => r.id === rowId);
  if (rowIndex < 0) return { gridState, ledger, applied: 0 };

  const row = { ...values[rowIndex] };
  const unassigned = readCell(row, unassignedColumnRowId);
  const order = unassignedFundingOrder(destHourType);
  const { ledger: nextLedger, entry } = ensureLedgerEntry(
    ledger,
    rowId,
    destinationColumnRowId,
    destHourType,
  );

  let remaining = delta;
  while (remaining > 0) {
    let tookThisRound = 0;
    for (const sourceType of order) {
      const avail = unassigned[sourceType];
      if (avail <= 0) continue;
      const chunk = Math.min(remaining, avail);
      pushTake(entry, sourceType, chunk);
      unassigned[sourceType] -= chunk;
      tookThisRound += chunk;
      remaining -= chunk;
      if (remaining <= 0) break;
    }

    if (tookThisRound <= 0 && remaining > 0) {
      pushTake(entry, order[0], remaining);
      unassigned[order[0]] -= remaining;
      remaining = 0;
      break;
    }
  }

  const applied = delta - remaining;
  if (applied <= 0) {
    return { gridState, ledger, applied: 0 };
  }

  const dest = readCell(row, destinationColumnRowId);
  dest[destHourType] += applied;

  let nextRow = writeCell(row, unassignedColumnRowId, unassigned);
  nextRow = writeCell(nextRow, destinationColumnRowId, dest);

  const nextValues = [...values];
  nextValues[rowIndex] = nextRow;

  return {
    gridState: { ...nextGrid, values: nextValues },
    ledger: nextLedger,
    applied,
  };
}

export function subtractHours({
  gridState,
  ledger,
  rowId,
  unassignedColumnRowId,
  destinationColumnRowId,
  destHourType,
  delta,
  sameProjectCostcodedColumnIds,
}) {
  const colSet = new Set(sameProjectCostcodedColumnIds || []);
  if (delta <= 0 || !colSet.has(destinationColumnRowId)) {
    return { gridState, ledger, applied: 0 };
  }

  const nextGrid = cloneDeep(gridState);
  const values = nextGrid.values || [];
  const rowIndex = values.findIndex((r) => r.id === rowId);
  if (rowIndex < 0) return { gridState, ledger, applied: 0 };

  const row = { ...values[rowIndex] };
  const dest = readCell(row, destinationColumnRowId);
  const destBefore = dest[destHourType];
  const actualSubtract = Math.min(delta, destBefore);
  if (actualSubtract <= 0) {
    return { gridState, ledger, applied: 0 };
  }

  const { ledger: nextLedger, entry } = ensureLedgerEntry(
    ledger,
    rowId,
    destinationColumnRowId,
    destHourType,
  );
  const stackTotal = sumStack(entry);
  const refundAmt = Math.min(actualSubtract, stackTotal);

  const refunds = refundAmt > 0
    ? popFromStack(entry, refundAmt)
    : { hours: 0, otHours: 0, doubleOTHours: 0 };

  const untrackedRemainder = actualSubtract - refundAmt;
  if (untrackedRemainder > 0) {
    refunds[destHourType] += untrackedRemainder;
  }

  const unassigned = readCell(row, unassignedColumnRowId);
  hourTypes.forEach((hourTypeKey) => {
    unassigned[hourTypeKey] += refunds[hourTypeKey];
  });

  dest[destHourType] -= actualSubtract;

  let nextRow = writeCell(row, unassignedColumnRowId, unassigned);
  nextRow = writeCell(nextRow, destinationColumnRowId, dest);

  const nextValues = [...values];
  nextValues[rowIndex] = nextRow;

  return {
    gridState: { ...nextGrid, values: nextValues },
    ledger: nextLedger,
    applied: actualSubtract,
  };
}

function sumCostCodedBreakdown(columns, row) {
  return columns.reduce(
    (acc, column) => {
      const cell = readCell(row, column.rowId);
      acc.hours += cell.hours;
      acc.otHours += cell.otHours;
      acc.doubleOTHours += cell.doubleOTHours;
      acc.total += cell.hours + cell.otHours + cell.doubleOTHours;
      return acc;
    },
    { hours: 0, otHours: 0, doubleOTHours: 0, total: 0 },
  );
}

export function getRowTotals({
  gridState,
  originalGridState,
  rowId,
  unassignedColumnRowId,
  activeProjectId,
}) {
  const columns = gridState?.columns || [];
  const values = gridState?.values || [];
  const row = values.find((r) => r.id === rowId);

  const unassignedCell = readCell(row || {}, unassignedColumnRowId);
  const sameProjectCols = getSameProjectCostcodedColumns(columns, activeProjectId);
  const costCoded = sumCostCodedBreakdown(sameProjectCols, row || {});

  const unassignedSum = sumCellHours(unassignedCell);
  const costCodedSum = sumCellHours(costCoded);
  const poolTotal = Math.max(0, unassignedSum) + costCodedSum;
  let originalPoolTotal = poolTotal;
  let overrun = false;
  let overrunBy = 0;

  if (originalGridState) {
    const origValues = originalGridState?.values || [];
    const origRow = origValues.find((r) => r.id === rowId);
    const origUnassigned = readCell(origRow || {}, unassignedColumnRowId);
    const origSameCols = getSameProjectCostcodedColumns(
      originalGridState?.columns || columns,
      activeProjectId,
    );
    const origCostCoded = sumCostCodedBreakdown(origSameCols, origRow || {});
    originalPoolTotal = sumCellHours(origUnassigned) + sumCellHours(origCostCoded);
    const roundedUnassignedSum = roundHours(unassignedSum);
    overrunBy = roundedUnassignedSum < 0 ? -roundedUnassignedSum : 0;
    overrun = overrunBy > 0;
  }

  return {
    unassigned: unassignedCell,
    costCoded,
    overrun,
    overrunBy,
    poolTotal,
    originalPoolTotal,
  };
}
